import {
	type Command,
	containerRows,
	type Replica,
	type SubtreeDraft,
	subtreeIds,
	subtreeSnapshot
} from "@mapcraft-tasks/client-store";
import { useDispatch, useReplica, useStore, useToday } from "@mapcraft-tasks/client-store/react";
import {
	countDrafts,
	DELETE_UNDO_LIMIT,
	DELETE_UNDO_ROW_LIMIT,
	DELETE_UNDO_SIZE_LIMIT,
	INSERT_SUBTREE_CONFIRM_THRESHOLD,
	type NodeStatus,
	newNodeId,
	type OutlineDraft,
	parseOutlineText,
	SYSTEM_NODE_IDS,
	type UserRole
} from "@mapcraft-tasks/domain";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore
} from "react";
import { toggleDoneCommands } from "../lib/complete";
import { shortcutChar } from "../lib/keyboard";
import { playComplete } from "../lib/sound";
import { clipboardStore } from "./clipboard";
import { collapseStore } from "./collapse";
import { containerKeyOf, flatten, indexOfKey, type Row } from "./flatten";
import { type UndoCommand, UndoStack } from "./undo";

/**
 * Модальная модель ввода — как в Checkvist, а не как в Workflowy.
 *
 * По умолчанию человек НЕ в редакторе: стрелки двигают выделение, редактирование
 * включается явно. Выигрыш не в стиле, а в том, что в режиме навигации свободен весь
 * алфавит — команды задаются двухбуквенными мнемоническими аккордами (`dd`, `tt`, `nn`),
 * а не трёхэтажными модификаторами, конфликтующими с браузером и ОС (ui-spec Область 2).
 *
 * Побочный выигрыш: слэш-команды не нужны вовсе — их работу делают аккорды.
 */

export type Mode = "nav" | "edit";

export type Overlay =
	| null
	| { kind: "status" }
	| { kind: "role" }
	| { kind: "date" }
	| { kind: "recurrence" }
	| { kind: "tags" }
	| { kind: "palette" }
	| { kind: "search" }
	| { kind: "picker"; purpose: "move" | "placement" }
	| { kind: "confirm"; message: string; confirmLabel: string; danger?: boolean; onConfirm: () => void };

export type OutlineState = {
	rows: Row[];
	mode: Mode;
	cursorKey: string | null;
	selectedKeys: ReadonlySet<string>;
	chord: string;
	/** `caret` числом — точное смещение: им отступ возвращает каретку туда, где она была. */
	editing: { key: string; caret: "start" | "end" | number | null } | null;
	overlay: Overlay;
	openBodies: ReadonlySet<string>;
	flashing: ReadonlySet<string>;
	rootId: string;
};

export type OutlineActions = ReturnType<typeof useOutlineEngine>["actions"];

const OutlineContext = createContext<{ state: OutlineState; actions: OutlineActions } | null>(null);

export function OutlineProvider({
	value,
	children
}: {
	value: { state: OutlineState; actions: OutlineActions };
	children: ReactNode;
}) {
	return <OutlineContext.Provider value={value}>{children}</OutlineContext.Provider>;
}

export function useOutline() {
	const value = useContext(OutlineContext);
	if (!value) {
		throw new Error("OutlineProvider не смонтирован");
	}
	return value;
}

/** Сколько ждём вторую букву аккорда, прежде чем забыть первую. */
const CHORD_TIMEOUT_MS = 1400;

/**
 * «Отметить выполненной» — это две разные команды: галочка (`completeNode`, она же заводит
 * запись о выполнении) и статус «сделано» из меню. Обе — одно и то же намерение человека,
 * поэтому и звучат одинаково. Снятие галочки и «не буду делать» сюда не попадают.
 */
function isCompletion(command: Command): boolean {
	return command.name === "completeNode" || (command.name === "setStatusMany" && command.input.status === "done");
}

/** Родные дети контейнера по порядку. Вплетения отсеиваются: они принадлежат другому дому. */
function ownChildren(replica: Replica, containerId: string): string[] {
	return containerRows(replica, containerId)
		.filter((row) => row.placementId === null)
		.map((row) => row.id);
}

type FullDraft = { id: string; title: string; body: string | null; children: FullDraft[] };

/**
 * Копия ноды — это копия ПОДДЕРЕВА: человек копирует пункт вместе с тем, что под ним,
 * плоская копия одного заголовка неотличима от вставки текста.
 *
 * Спускаемся только по родным детям (`parent_id`): вплетение — чужая нода, её дубликат
 * был бы уже не «показать здесь», а вторая сущность. Вплетения и completions не копируются
 * (ui-spec 3.5); родное дерево ациклично, поэтому рекурсия безопасна.
 */
function duplicateSubtree(replica: Replica, nodeId: string): FullDraft | null {
	const node = replica.nodes.get(nodeId);
	if (!node) {
		return null;
	}
	return {
		id: newNodeId(),
		title: node.title,
		body: node.body,
		children: ownChildren(replica, nodeId)
			.map((id) => duplicateSubtree(replica, id))
			.filter((draft) => draft !== null)
	};
}

/**
 * Команды, возвращающие ноды туда, откуда их сейчас заберёт `moveNodes` — обратный ход
 * для `Cmd+x` → `Cmd+v`.
 *
 * Снимок обязан сниматься ДО перемещения: после него прежних соседей уже не спросишь.
 * Возврат идёт ПО ОДНОЙ ноде в исходном порядке, а не одной пачкой: вырезанные строки
 * не обязаны быть соседними, а так каждая следующая цепляется за предыдущую, уже
 * вернувшуюся, и порядок контейнера восстанавливается целиком.
 *
 * Якорь всегда существует на момент выполнения своей команды: `after` — это либо сосед
 * вне набора (он никуда не уезжал), либо сосед из набора, вернувшийся на шаг раньше.
 * Первую строку контейнера цеплять не за что, поэтому она идёт `before` ближайшего
 * соседа ВНЕ набора; если таких нет — весь контейнер и был набором, и место в конце
 * равно прежнему.
 */
function restoreCommands(replica: Replica, nodeIds: readonly string[]): Command[] {
	const moving = new Set(nodeIds);
	const byParent = new Map<string, string[]>();
	for (const id of nodeIds) {
		const parentId = replica.nodes.get(id)?.parentId;
		if (!parentId) {
			continue; // корня и потерянных нод перенос не касается
		}
		byParent.set(parentId, [...(byParent.get(parentId) ?? []), id]);
	}

	const commands: Command[] = [];
	for (const [parentId, ids] of byParent) {
		const siblings = ownChildren(replica, parentId);
		const ordered = [...ids].sort((a, b) => siblings.indexOf(a) - siblings.indexOf(b));
		for (const id of ordered) {
			const index = siblings.indexOf(id);
			const after = index > 0 ? siblings[index - 1] : undefined;
			const before = after ? undefined : siblings.slice(index + 1).find((s) => !moving.has(s));
			commands.push({ name: "moveNodes", input: { nodeIds: [id], parentId, after, before } });
		}
	}
	return commands;
}

/**
 * Представление для СИСТЕМНОГО буфера: табулированный текст (ui-spec 3.5). Отступы — то,
 * что на той стороне снова разберётся в дерево (`parseOutlineText`, 3.6).
 */
function subtreeText(replica: Replica, nodeIds: readonly string[], depth = 0): string[] {
	const lines: string[] = [];
	for (const id of nodeIds) {
		const node = replica.nodes.get(id);
		if (!node) {
			continue;
		}
		lines.push("\t".repeat(depth) + node.title);
		lines.push(...subtreeText(replica, ownChildren(replica, id), depth + 1));
	}
	return lines;
}

export function useOutlineEngine(options: {
	rootId: string;
	onZoom: (nodeId: string) => void;
	onGo: (to: string) => void;
	/**
	 * Корень — day-нода этой даты. Просмотр день НЕ создаёт, но первая запись обязана
	 * положить `ensureDay` перед вставкой ребёнка: иначе ребёнок повиснет на родителе,
	 * которого на сервере нет (ui-spec Область 5, offline-sync §8).
	 */
	ensureDate?: string;
}) {
	const store = useStore();
	const dispatch = useDispatch();
	const today = useToday();
	const replica = useReplica();

	const [mode, setMode] = useState<Mode>("nav");
	const [cursorKey, setCursorKey] = useState<string | null>(null);
	const [anchorKey, setAnchorKey] = useState<string | null>(null);
	const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set());
	const [chord, setChord] = useState("");
	const [editing, setEditing] = useState<OutlineState["editing"]>(null);
	const [overlay, setOverlay] = useState<Overlay>(null);
	const [openBodies, setOpenBodies] = useState<ReadonlySet<string>>(new Set());
	const [flashing, setFlashing] = useState<ReadonlySet<string>>(new Set());
	const [collapseTick, setCollapseTick] = useState(0);
	// Буфер — вне страницы: он обязан пережить переход «скопировал тут, вставляю там».
	const clipboard = useSyncExternalStore(clipboardStore.subscribe, clipboardStore.getSnapshot);
	const setClipboard = clipboardStore.set;

	const chordTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const undoStack = useRef(new UndoStack());

	const rows = useMemo(() => {
		void collapseTick;
		return flatten({ replica, rootId: options.rootId, isCollapsed: collapseStore.isCollapsed });
	}, [replica, options.rootId, collapseTick]);

	const rowByKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows]);
	const cursor = cursorKey ? rowByKey.get(cursorKey) : undefined;

	// --- выделение ---------------------------------------------------------

	/**
	 * Мультивыделение ограничено ОДНИМ родителем: выделение из разных контейнеров даёт
	 * непонятный интерфейс — неясно, что и куда поедет (ui-spec 3.2).
	 */
	const selectionRows = useCallback((): Row[] => {
		if (selectedKeys.size === 0) {
			return cursor ? [cursor] : [];
		}
		return rows.filter((r) => selectedKeys.has(r.key));
	}, [rows, selectedKeys, cursor]);

	const selectionIds = useCallback(() => [...new Set(selectionRows().map((r) => r.nodeId))], [selectionRows]);

	const moveCursor = useCallback(
		(delta: number, extend = false) => {
			if (rows.length === 0) {
				return;
			}
			const index = indexOfKey(rows, cursorKey);
			const next = Math.min(rows.length - 1, Math.max(0, (index === -1 ? 0 : index) + delta));
			const target = rows[next]!;

			if (!extend) {
				setCursorKey(target.key);
				setAnchorKey(target.key);
				setSelectedKeys(new Set());
				return;
			}

			// Расширение идёт ПО СОСЕДЯМ ОДНОГО УРОВНЯ, перешагивая раскрытые вложенные
			// строки: они всё равно поедут вместе со своим родителем.
			const anchor = anchorKey ? rowByKey.get(anchorKey) : cursor;
			if (!anchor) {
				return;
			}
			const siblings = rows.filter((r) => r.containerId === anchor.containerId);
			const from = siblings.findIndex((r) => r.key === anchor.key);
			const cur = siblings.findIndex((r) => r.key === (cursorKey ?? anchor.key));
			const to = Math.min(siblings.length - 1, Math.max(0, cur + delta));
			if (from === -1 || to === -1) {
				return;
			}
			const [lo, hi] = from <= to ? [from, to] : [to, from];
			setSelectedKeys(new Set(siblings.slice(lo, hi + 1).map((r) => r.key)));
			setCursorKey(siblings[to]!.key);
		},
		[rows, rowByKey, cursorKey, anchorKey, cursor]
	);

	const focusRow = useCallback((key: string) => {
		setCursorKey(key);
		setAnchorKey(key);
		setSelectedKeys(new Set());
	}, []);

	/**
	 * Снять выделение совсем — щелчок мимо списка. Курсор гасится вместе с набором:
	 * подсветку строке даёт и он тоже (`cursorKey === row.key`), и оставить его значило бы
	 * «снял, а строка всё равно горит». Клавиатура после этого не ломается: стрелка встаёт
	 * на первую строку, `Enter` заводит новую в конце — курсора-то нет.
	 */
	const clearSelection = useCallback(() => {
		setCursorKey(null);
		setAnchorKey(null);
		setSelectedKeys(new Set());
	}, []);

	/**
	 * `Shift`-щелчок — то же расширение, что и `Shift+↑/↓`: диапазон СОСЕДЕЙ от якоря до
	 * нажатой строки, перешагивая раскрытые ветки внутри (они поедут вместе с родителем).
	 *
	 * Через границу контейнера диапазон не строится: выделение живёт в одном родителе
	 * (ui-spec 3.2), «отсюда и до чужого поддерева» модель не выражает. Такой щелчок просто
	 * переносит якорь — это предсказуемее, чем молча выделить что-то другое.
	 */
	const selectTo = useCallback(
		(key: string) => {
			const target = rowByKey.get(key);
			if (!target) {
				return;
			}
			const anchor = (anchorKey ? rowByKey.get(anchorKey) : undefined) ?? cursor;
			if (!anchor || anchor.containerId !== target.containerId) {
				focusRow(key);
				return;
			}
			const siblings = rows.filter((r) => r.containerId === anchor.containerId);
			const from = siblings.findIndex((r) => r.key === anchor.key);
			const to = siblings.findIndex((r) => r.key === key);
			if (from === -1 || to === -1) {
				return;
			}
			const [lo, hi] = from <= to ? [from, to] : [to, from];
			setSelectedKeys(new Set(siblings.slice(lo, hi + 1).map((r) => r.key)));
			setCursorKey(key); // якорь на месте: следующий `Shift`-щелчок тянет от него же
		},
		[rows, rowByKey, anchorKey, cursor, focusRow]
	);

	/**
	 * `Cmd`-щелчок — добавить строку к выделению или убрать её оттуда.
	 *
	 * Отсчёт идёт от `selectionRows()`, а не от `selectedKeys`: пока множество пусто,
	 * выделением считается строка под курсором — иначе первый же `Cmd`-щелчок по соседу
	 * терял бы то, что человек видит выделенным.
	 */
	const toggleSelect = useCallback(
		(key: string) => {
			const target = rowByKey.get(key);
			if (!target) {
				return;
			}
			const base = selectionRows();
			const first = base[0];
			if (first && first.containerId !== target.containerId) {
				focusRow(key); // чужой контейнер — выделение начинается заново
				return;
			}
			const keys = new Set(base.map((r) => r.key));
			if (keys.delete(key)) {
				const rest = [...keys];
				if (rest.length <= 1) {
					// Одна строка — это уже не мультивыделение, а обычный курсор.
					focusRow(rest[0] ?? key);
					return;
				}
				setSelectedKeys(keys);
				if (cursorKey === key) {
					const last = rest[rest.length - 1]!;
					setCursorKey(last);
					setAnchorKey(last);
				}
				return;
			}
			keys.add(key);
			setSelectedKeys(keys);
			// Якорем становится последняя нажатая — как в файловых менеджерах: `Cmd`, потом `Shift`.
			setCursorKey(key);
			setAnchorKey(key);
		},
		[rowByKey, selectionRows, cursorKey, focusRow]
	);

	/**
	 * Куда сядет курсор, когда строки под ним не станет. Ближайшая уцелевшая выше, а если
	 * выше ничего нет — ближайшая ниже. В плоском списке «выше» — это предыдущий сосед либо
	 * родитель, то есть ровно то, что ждёшь после `Esc` из черновика или `Del`.
	 *
	 * Без этого `cursorKey` повисает на исчезнувшем ключе: подсветка пропадает, а первая же
	 * стрелка уводит в начало списка.
	 */
	const focusAfterRemoval = useCallback(
		(keys: readonly string[]) => {
			if (cursorKey === null || !keys.includes(cursorKey)) {
				return; // курсор уже переехал сам (например, кликом мыши) — не перебиваем
			}
			const index = indexOfKey(rows, cursorKey);
			if (index === -1) {
				return;
			}
			// Вместе со строкой уезжают её потомки. Ключ строки — путь, потомок узнаётся по префиксу.
			const survives = (key: string) => !keys.some((k) => key === k || key.startsWith(`${k}/`));
			const above = rows.slice(0, index).filter((r) => survives(r.key));
			const next = above[above.length - 1] ?? rows.slice(index + 1).find((r) => survives(r.key));

			setCursorKey(next?.key ?? null);
			setAnchorKey(next?.key ?? null);
			setSelectedKeys(new Set());
		},
		[rows, cursorKey]
	);

	/**
	 * Отступ и выступ не создают и не убирают ноду — они меняют ЕЁ ПУТЬ, а ключ строки это
	 * путь и есть. Курсор, выделение и открытый редактор держатся за ключи, поэтому после
	 * переезда их надо перевести на новые: иначе строка на экране осталась, а фокус с неё
	 * слетел — подсветки нет, редактор закрылся, следующая стрелка уводит в начало списка.
	 *
	 * `containerKey` — путь НОВОГО контейнера; строки едут в него все разом (выделение
	 * ограничено одним родителем), поэтому новый ключ каждой — `containerKey/nodeId`.
	 */
	const rekeyFocus = useCallback((containerKey: string, moved: readonly Row[]) => {
		const next = new Map(moved.map((r) => [r.key, `${containerKey}/${r.nodeId}`]));
		const remap = (key: string) => next.get(key) ?? key;

		setCursorKey((key) => (key === null ? null : remap(key)));
		setAnchorKey((key) => (key === null ? null : remap(key)));
		setSelectedKeys((keys) => (keys.size === 0 ? keys : new Set([...keys].map(remap))));
		setEditing((editing) => (editing === null ? null : { ...editing, key: remap(editing.key) }));
		setOpenBodies((keys) => (keys.size === 0 ? keys : new Set([...keys].map(remap))));
	}, []);

	/**
	 * Выход из редактора засчитывается, только если он идёт СО СВОЕЙ строки.
	 *
	 * `Tab` переводит `editing` на новый ключ ещё до того, как старый редактор снимут с
	 * экрана. Его прощальный `blur` (если браузер вообще его пошлёт) обязан оказаться
	 * холостым: иначе он закроет уже открытый редактор на новом месте, а `discardDraft`
	 * заодно снесёт только что сдвинутый пустой черновик.
	 */
	const editingRef = useRef(editing);
	editingRef.current = editing;

	const stopEditing = useCallback((key: string): boolean => {
		if (editingRef.current !== null && editingRef.current.key !== key) {
			return false;
		}
		setMode("nav");
		setEditing(null);
		return true;
	}, []);

	const flash = useCallback((keys: readonly string[]) => {
		setFlashing(new Set(keys));
		setTimeout(() => setFlashing(new Set()), 700);
	}, []);

	// --- команды -----------------------------------------------------------

	const run = useCallback(
		(commands: Command[], undo?: { label: string; commands: UndoCommand[] }) => {
			for (const command of commands) {
				dispatch(command);
			}
			// Звук «сделано» живёт здесь, на самой отправке команды, а не в обработчиках
			// пробела и щелчка: отметить выполненной можно галочкой, пробелом и меню статусов,
			// и все пути сходятся сюда — иначе каждый новый пришлось бы озвучивать заново.
			// Отмена (`undo`) идёт мимо `run`, прямым `dispatch`, и остаётся молчаливой.
			if (commands.some(isCompletion)) {
				playComplete();
			}
			if (undo) {
				undoStack.current.push(undo);
			}
		},
		[dispatch]
	);

	const createSibling = useCallback(
		(where: "below" | "above" | "child") => {
			const id = newNodeId();
			const parentId =
				where === "child" ? (cursor?.nodeId ?? options.rootId) : (cursor?.containerId ?? options.rootId);

			// День заводится ПЕРВОЙ ЗАПИСЬЮ, а не просмотром: иначе Календарь засорялся бы
			// пустыми днями. id детерминирован, поэтому ребёнок уже знает своего родителя.
			if (options.ensureDate && !replica.nodes.has(options.rootId)) {
				dispatch({ name: "ensureDay", input: { date: options.ensureDate } });
			}
			const place =
				where === "below" && cursor
					? { after: cursor.nodeId }
					: where === "above" && cursor
						? { before: cursor.nodeId }
						: undefined;

			run([{ name: "createNode", input: { id, parentId, title: "", place } }], {
				label: "новая нода",
				commands: [{ name: "deleteNodes", input: { nodeIds: [id] } }]
			});

			if (where === "child" && cursor) {
				collapseStore.set_(cursor.key, false);
				setCollapseTick((t) => t + 1);
			}
			// Ключ новой строки станет известен после пересчёта — метим по id.
			pendingFocus.current = id;
			draftId.current = id;
			setMode("edit");
			setEditing({ key: "", caret: "end" });
		},
		[cursor, options.rootId, options.ensureDate, replica, dispatch, run]
	);

	/**
	 * Последняя заведённая строка, пока она пуста, — черновик. `Esc` из неё значит
	 * «передумал»: строку не оставляем. Оставить осознанно — `Enter`ом, он уводит из
	 * редактора не отменяя (ui-spec Область 2).
	 */
	const draftId = useRef<string | null>(null);

	const discardDraft = useCallback(
		(nodeId: string) => {
			if (draftId.current !== nodeId) {
				return;
			}
			draftId.current = null;

			const node = store.getSnapshot().replica.nodes.get(nodeId);
			if (node?.title.trim() !== "" || node.body !== null) {
				return;
			}
			if (rows.some((r) => r.containerId === nodeId)) {
				return; // успела обзавестись детьми — это уже не черновик
			}

			// Брошенный черновик не оставляет следа и в undo: снимаем собственную запись
			// о создании, иначе `Cmd+Z` отменил бы уже отменённое.
			undoStack.current.popIf((entry) =>
				entry.commands.some((c) => c.name === "deleteNodes" && c.input.nodeIds.includes(nodeId))
			);
			const row = rows.find((r) => r.nodeId === nodeId);
			if (row) {
				focusAfterRemoval([row.key]);
			}
			dispatch({ name: "deleteNodes", input: { nodeIds: [nodeId] } });
		},
		[store, rows, dispatch, focusAfterRemoval]
	);

	const pendingFocus = useRef<string | null>(null);
	if (pendingFocus.current) {
		const found = rows.find((r) => r.nodeId === pendingFocus.current);
		if (found) {
			pendingFocus.current = null;
			if (cursorKey !== found.key) {
				queueMicrotask(() => {
					setCursorKey(found.key);
					setAnchorKey(found.key);
					setSelectedKeys(new Set());
					setEditing({ key: found.key, caret: "end" });
				});
			}
		}
	}

	/**
	 * `Tab` — сделать ноду ребёнком предыдущего соседа. Работает НАД ВХОЖДЕНИЕМ:
	 * у родной ноды меняется `parent_id`, у вплетения — `container_id` (ui-spec 3.3).
	 */
	const indent = useCallback(() => {
		const selected = selectionRows();
		const first = selected[0];
		if (!first) {
			return;
		}
		const siblings = rows.filter((r) => r.containerId === first.containerId);
		const idx = siblings.findIndex((r) => r.key === first.key);
		const prev = siblings[idx - 1];
		if (!prev) {
			return;
		}
		const ids = selected.map((r) => r.nodeId);
		run([{ name: "moveNodes", input: { nodeIds: ids, parentId: prev.nodeId } }], {
			label: "отступ",
			commands: [{ name: "moveNodes", input: { nodeIds: ids, parentId: first.containerId, after: prev.nodeId } }]
		});
		collapseStore.set_(prev.key, false);
		setCollapseTick((t) => t + 1);
		// Строки уезжают внутрь `prev`, значит их новый контейнер — это сам `prev`.
		rekeyFocus(prev.key, selected);
	}, [rows, selectionRows, run, rekeyFocus]);

	const outdent = useCallback(() => {
		const selected = selectionRows();
		const first = selected[0];
		if (!first || first.depth === 0) {
			return;
		}
		const parentRow = rows.find((r) => r.nodeId === first.containerId);
		if (!parentRow) {
			return;
		}
		const ids = selected.map((r) => r.nodeId);
		run(
			[{ name: "moveNodes", input: { nodeIds: ids, parentId: parentRow.containerId, after: parentRow.nodeId } }],
			{
				label: "выступ",
				commands: [{ name: "moveNodes", input: { nodeIds: ids, parentId: first.containerId } }]
			}
		);
		// Строки поднимаются на уровень родителя, то есть в ЕГО контейнер. Путь берём из
		// собственного ключа строки, а не из `parentRow`: у ноды может быть несколько
		// вхождений, и `parentRow` — лишь первое найденное, не обязательно наше.
		rekeyFocus(containerKeyOf(containerKeyOf(first.key)), selected);
	}, [rows, selectionRows, run, rekeyFocus]);

	const moveVertically = useCallback(
		(direction: -1 | 1) => {
			const selected = selectionRows();
			const first = selected[0];
			const last = selected[selected.length - 1];
			if (!first || !last) {
				return;
			}
			const siblings = rows.filter((r) => r.containerId === first.containerId);
			const firstIdx = siblings.findIndex((r) => r.key === first.key);
			const lastIdx = siblings.findIndex((r) => r.key === last.key);
			const target = direction === -1 ? siblings[firstIdx - 1] : siblings[lastIdx + 1];
			if (!target) {
				return;
			}
			const ids = selected.map((r) => r.nodeId);
			const back = direction === -1 ? { after: target.nodeId } : { before: target.nodeId };
			run(
				[
					{
						name: "moveNodes",
						input: {
							nodeIds: ids,
							parentId: first.containerId,
							...(direction === -1 ? { before: target.nodeId } : { after: target.nodeId })
						}
					}
				],
				{
					label: "перестановка",
					commands: [{ name: "moveNodes", input: { nodeIds: ids, parentId: first.containerId, ...back } }]
				}
			);
		},
		[rows, selectionRows, run]
	);

	/**
	 * `Space` работает по ВЫДЕЛЕНИЮ, а нажатая галочка — по СВОЕЙ строке, и путать их нельзя.
	 *
	 * Под пальцем выделения может не быть вовсе: тап по тексту открывает правку, а тап по
	 * галочке до строки не доходит (она гасит всплытие), — курсор так и остаётся пустым,
	 * и галочка по выделению молча не срабатывала. Мышь это скрывала: у неё `mousedown`
	 * строки успевает подвинуть курсор до `click` по галочке.
	 */
	const toggleDone = useCallback(
		(key?: string) => {
			const target = key === undefined ? undefined : rowByKey.get(key);
			const selected = key === undefined ? selectionRows() : target ? [target] : [];
			for (const row of selected) {
				// Что именно значит «сделал» для этой ноды — знает `toggleDoneCommands`,
				// одинаково для строки, заголовка страницы и панелей (lib/complete).
				// Здесь остаётся только отмена: она есть у аутлайна и больше нигде.
				const step = toggleDoneCommands(row.node);
				if (step === null) {
					continue;
				}
				run([step.forward], { label: step.label, commands: [step.back] });
			}
			flash(selected.map((r) => r.key));
		},
		[selectionRows, rowByKey, run, flash]
	);

	const setStatus = useCallback(
		(status: NodeStatus | null) => {
			const selected = selectionRows();
			const ids = selected.map((r) => r.nodeId);
			if (ids.length === 0) {
				return;
			}
			const before = selected.map((r) => ({ id: r.nodeId, status: r.node.status }));
			run([{ name: "setStatusMany", input: { nodeIds: ids, status } }], {
				label: "статус",
				commands: before.map(
					(b) => ({ name: "setStatusMany", input: { nodeIds: [b.id], status: b.status } }) as Command
				)
			});
			setOverlay(null);
		},
		[selectionRows, run]
	);

	const setRole = useCallback(
		(role: UserRole) => {
			const selected = selectionRows();
			run(
				selected.map((r) => ({ name: "convertRole", input: { nodeId: r.nodeId, role } }) as Command),
				{
					label: "роль",
					commands: selected
						.filter((r) => r.node.role !== "recurring")
						.map(
							(r) =>
								({
									name: "convertRole",
									input: { nodeId: r.nodeId, role: r.node.role as UserRole }
								}) as Command
						)
				}
			);
			setOverlay(null);
		},
		[selectionRows, run]
	);

	const schedule = useCallback(
		(date: string | null) => {
			const selected = selectionRows();
			run(
				selected.map((r) => ({ name: "schedule", input: { nodeId: r.nodeId, date } }) as Command),
				{
					label: "срок",
					commands: selected.map(
						(r) =>
							({ name: "schedule", input: { nodeId: r.nodeId, date: r.node.scheduledDate } }) as Command
					)
				}
			);
			setOverlay(null);
		},
		[selectionRows, run]
	);

	const archiveOrRestore = useCallback(() => {
		const selected = selectionRows();
		const ids = selected.map((r) => r.nodeId);
		if (ids.length === 0) {
			return;
		}
		const archived = selected.every((r) => r.node.archivedAt !== null);
		run(
			[
				archived
					? { name: "restoreNodes", input: { nodeIds: ids } }
					: { name: "archiveNodes", input: { nodeIds: ids } }
			],
			{
				label: archived ? "восстановление" : "архивация",
				commands: [
					archived
						? { name: "archiveNodes", input: { nodeIds: ids } }
						: { name: "restoreNodes", input: { nodeIds: ids } }
				]
			}
		);
	}, [selectionRows, run]);

	const removeSelection = useCallback(() => {
		const selected = selectionRows();
		if (selected.length === 0) {
			return;
		}

		// На строке-вплетении Del убирает ВПЛЕТЕНИЕ, а не ноду: она живёт дома
		// и видна в других местах (ui-spec 3.3).
		const placements = selected.filter((r) => r.placementId !== null);
		if (placements.length === selected.length) {
			run(
				placements.map(
					(r) =>
						({
							name: "removePlacement",
							input: { nodeId: r.nodeId, containerId: r.containerId }
						}) as Command
				),
				{
					label: "снятие вплетения",
					commands: placements.map(
						(r) =>
							({
								name: "addPlacement",
								input: { nodeId: r.nodeId, containerId: r.containerId }
							}) as Command
					)
				}
			);
			focusAfterRemoval(placements.map((r) => r.key));
			return;
		}

		const ids = selected.map((r) => r.nodeId);
		const empty = selected.every((r) => r.node.title.trim() === "" && r.node.body === null && !r.hasChildren);

		// Отменяемость решается ЗДЕСЬ, до подтверждения, и обратно не переигрывается:
		// команда «верни операцию X» либо ложится в стек сразу, либо не ложится вовсе.
		// Ответ сервера переписать её уже не может, а отмена, которая ничего не отменит,
		// хуже честного «этого вернуть будет нельзя».
		const doomed = subtreeIds(replica, ids);
		// Ноды считаются первыми и отдельно: собирать снимок ради заведомо неотменяемого
		// удаления незачем, это проход по всей реплике.
		const draft = doomed.length <= DELETE_UNDO_LIMIT ? subtreeSnapshot(replica, doomed) : null;
		const rows = draft
			? draft.nodes.length + draft.placements.length + draft.links.length + draft.completions.length
			: 0;
		// Второй предел — по строкам снимка: у одной ноды бывают тысячи выполнений и бэклинков.
		// Третий — по размеру: у заметки нет верхней длины, и одна нода с мегабайтным `body`
		// прошла бы оба предыдущих, а в IndexedDB уехала бы целиком.
		const size = draft && rows <= DELETE_UNDO_ROW_LIMIT ? JSON.stringify(draft).length : Number.POSITIVE_INFINITY;
		const undoable = draft !== null && rows <= DELETE_UNDO_ROW_LIMIT && size <= DELETE_UNDO_SIZE_LIMIT;
		const op = undoable ? crypto.randomUUID() : undefined;
		const snapshot = undoable ? draft : null;

		const remove = () => {
			run(
				[{ name: "deleteNodes", input: { nodeIds: ids, op } }],
				op && snapshot
					? { label: "удаление", commands: [{ name: "restoreDeleted", input: { op }, local: { snapshot } }] }
					: undefined
			);
			focusAfterRemoval(selected.map((r) => r.key));
			setOverlay(null);
		};

		// Пустая нода удаляется ТИХО, мимо архива — это «начал писать не то».
		if (empty) {
			remove();
			return;
		}
		setOverlay({
			kind: "confirm",
			message:
				(ids.length === 1
					? `Удалить «${selected[0]!.node.title}» и всё внутри?`
					: `Удалить ${ids.length} нод и всё внутри?`) +
				(undoable
					? ""
					: draft === null
						? ` Это ${doomed.length} нод — отменить такое удаление будет нельзя.`
						: " Слишком много данных — отменить такое удаление будет нельзя."),
			confirmLabel: "Удалить",
			danger: true,
			onConfirm: remove
		});
	}, [selectionRows, run, focusAfterRemoval, replica]);

	/**
	 * Пин — вплести выделенное в системный контейнер, а если уже вплетено, выплести.
	 * Жест остался один: фокус контейнером быть перестал и ставится теперь ролью (`rf`).
	 */
	const toggleSystemPlacement = useCallback(
		(containerId: string) => {
			const selected = selectionRows();
			for (const row of selected) {
				const placed = [...replica.placements.values()].some(
					(p) => p.nodeId === row.nodeId && p.containerId === containerId
				);
				run([
					placed
						? { name: "removePlacement", input: { nodeId: row.nodeId, containerId } }
						: { name: "addPlacement", input: { nodeId: row.nodeId, containerId } }
				]);
			}
			flash(selected.map((r) => r.key));
		},
		[selectionRows, replica, run, flash]
	);

	const togglePin = useCallback(() => toggleSystemPlacement(SYSTEM_NODE_IDS.pinned), [toggleSystemPlacement]);

	const toggleBody = useCallback(() => {
		if (!cursor) {
			return;
		}
		setOpenBodies((prev) => {
			const next = new Set(prev);
			if (next.has(cursor.key)) {
				next.delete(cursor.key);
			} else {
				next.add(cursor.key);
			}
			return next;
		});
	}, [cursor]);

	/**
	 * Закрыть заметку конкретной строки — из неё самой, а не «повторным `nn`».
	 *
	 * Пока выход был только через `nn`, из открытого поля выйти было нечем: клавиши в
	 * `textarea` до аккордов не доходят, а `nn` переключает строку ПОД КУРСОРОМ — стоило
	 * увести курсор, и поле оставалось открытым навсегда, накапливаясь по одному на строку.
	 */
	const closeBody = useCallback((key: string) => {
		setOpenBodies((prev) => {
			if (!prev.has(key)) {
				return prev;
			}
			const next = new Set(prev);
			next.delete(key);
			return next;
		});
	}, []);

	// --- буфер обмена ------------------------------------------------------

	/**
	 * `Cmd+x` НИЧЕГО не делает в момент нажатия — только помечает строки; move происходит
	 * на вставке. Иначе передумавший человек остаётся без нод (ui-spec 3.5).
	 */
	const copySelection = useCallback(
		(kind: "copy" | "cut") => {
			const selected = selectionRows();
			if (selected.length === 0) {
				return;
			}
			const nodeIds = selected.map((r) => r.nodeId);
			setClipboard({ mode: kind, nodeIds });
			void navigator.clipboard?.writeText(subtreeText(replica, nodeIds).join("\n")).catch(() => {});
			flash(selected.map((r) => r.key));
		},
		[selectionRows, flash, replica]
	);

	const pasteText = useCallback(
		(text: string) => {
			const drafts = parseOutlineText(text);
			if (drafts.length === 0) {
				return;
			}
			const total = countDrafts(drafts);
			// id раздаются ЗДЕСЬ, а не внутри `insert`: отмена вставки — это удаление ровно
			// тех нод, которые вставились, и корни надо знать до отправки команды.
			const withIds = (list: readonly OutlineDraft[]): SubtreeDraft[] =>
				list.map((d) => ({ id: newNodeId(), title: d.title, children: withIds(d.children) }));
			const nodes = withIds(drafts);
			const insert = () => {
				const parentId = cursor?.containerId ?? options.rootId;
				run(
					[
						{
							name: "insertSubtree",
							input: { parentId, nodes, place: cursor ? { after: cursor.nodeId } : undefined }
						}
					],
					{
						label: "вставка",
						commands: [{ name: "deleteNodes", input: { nodeIds: nodes.map((n) => n.id) } }]
					}
				);
				setOverlay(null);
			};
			// Мягкий порог: защита от случайной вставки простыни, а не технический предел.
			if (total > INSERT_SUBTREE_CONFIRM_THRESHOLD) {
				setOverlay({
					kind: "confirm",
					message: `Вставить ${total} строк?`,
					confirmLabel: "Вставить",
					onConfirm: insert
				});
				return;
			}
			insert();
		},
		[cursor, options.rootId, run]
	);

	/**
	 * Буфер приходит аргументом, а не берётся из состояния: `Cmd+d` дублирует немедленно,
	 * а поставленный тем же обработчиком `setClipboard` виден только со следующего рендера.
	 */
	const pasteNodes = useCallback(
		(entry: { mode: "copy" | "cut"; nodeIds: string[] }) => {
			const parentId = cursor?.containerId ?? options.rootId;
			if (entry.mode === "cut") {
				// Вырезание ОБЯЗАНО сохранять id: при delete+create порвались бы меншены,
				// вплетения и история completions. Это требование целостности, не оптимизация.
				const back = restoreCommands(replica, entry.nodeIds);
				run(
					[
						{
							name: "moveNodes",
							input: { nodeIds: entry.nodeIds, parentId, after: cursor?.nodeId }
						}
					],
					back.length > 0 ? { label: "перенос", commands: back } : undefined
				);
				return;
			}
			const drafts = entry.nodeIds.map((id) => duplicateSubtree(replica, id)).filter((draft) => draft !== null);
			if (drafts.length === 0) {
				return;
			}
			const insert = () =>
				run(
					[
						{
							name: "insertSubtree",
							input: { parentId, nodes: drafts, place: cursor ? { after: cursor.nodeId } : undefined }
						}
					],
					// Отмена сносит вставленные корни целиком: id у копии свои, чужого под
					// ними нет — `deleteNodes` каскадом уберёт ровно то, что приехало.
					{
						label: "вставка",
						commands: [{ name: "deleteNodes", input: { nodeIds: drafts.map((d) => d.id) } }]
					}
				);
			// Тот же мягкий порог, что и у текстовой вставки: дубликат проекта — тоже простыня.
			const total = countDrafts(drafts);
			if (total > INSERT_SUBTREE_CONFIRM_THRESHOLD) {
				setOverlay({
					kind: "confirm",
					message: `Вставить ${total} строк?`,
					confirmLabel: "Вставить",
					onConfirm: insert
				});
				return;
			}
			insert();
		},
		[cursor, options.rootId, run, replica]
	);

	/**
	 * `Cmd+Shift+V` — вставить ССЫЛКОЙ. «Скопировать» и «показать ещё здесь» — разные
	 * намерения, и развести их лучше там, где человек уже держит буфер: обычная вставка
	 * заводит вторую ноду, эта — вплетает ту же самую (ui-spec 3.5, 8.1). Аккорд `pl`
	 * остаётся для случая «вплести не сюда, а вон туда» — там нужен выбор контейнера.
	 *
	 * Режим буфера не смотрим: вплетение ничего не переносит, поэтому после `Cmd+x`
	 * оно означает ровно то же самое — нода остаётся дома и появляется ещё и здесь.
	 */
	const pasteLink = useCallback(() => {
		if (!clipboard) {
			return; // у внешнего текста нет id — вплетать нечего
		}
		const containerId = cursor?.containerId ?? options.rootId;
		const alreadyHere = (id: string) => {
			const node = replica.nodes.get(id);
			if (!node) {
				return true;
			}
			// Инварианты сервера, проверенные заранее: в себя не вплетают, а в родном доме
			// нода уже есть — родное родительство и вплетение в один контейнер взаимоисключающи
			// (data-model §2.2). Без этой проверки операция уехала бы в очередь и вернулась
			// конфликтом, то есть человек получил бы красный тост вместо «оно и так тут».
			return (
				id === containerId ||
				node.parentId === containerId ||
				[...replica.placements.values()].some((p) => p.nodeId === id && p.containerId === containerId)
			);
		};

		const fresh = clipboard.nodeIds.filter((id) => !alreadyHere(id));
		if (fresh.length === 0) {
			// Вплетать нечего — подсвечиваем те вхождения, которые здесь уже есть: молчание
			// человек прочитал бы как «клавиша не работает».
			flash(
				rows
					.filter((r) => r.containerId === containerId && clipboard.nodeIds.includes(r.nodeId))
					.map((r) => r.key)
			);
			return;
		}
		run(
			fresh.map(
				(id) =>
					({
						name: "addPlacement",
						input: { nodeId: id, containerId, place: cursor ? { after: cursor.nodeId } : undefined }
					}) as Command
			),
			{
				label: "вплетение",
				commands: fresh.map(
					(id) => ({ name: "removePlacement", input: { nodeId: id, containerId } }) as Command
				)
			}
		);
		setClipboard(null);
	}, [clipboard, cursor, options.rootId, replica, rows, run, flash]);

	const paste = useCallback(async () => {
		if (clipboard) {
			pasteNodes(clipboard);
			setClipboard(null);
			return;
		}
		const text = await navigator.clipboard?.readText().catch(() => "");
		if (text) {
			pasteText(text);
		}
	}, [clipboard, pasteNodes, pasteText]);

	const undo = useCallback(() => {
		const entry = undoStack.current.pop();
		if (!entry) {
			return;
		}
		for (const { local, ...command } of entry.commands) {
			dispatch(command as Command, local);
		}
	}, [dispatch]);

	// --- клавиатура --------------------------------------------------------

	const resetChord = useCallback(() => {
		if (chordTimer.current) {
			clearTimeout(chordTimer.current);
			chordTimer.current = null;
		}
		setChord("");
	}, []);

	const edit = useCallback(
		(caret: "start" | "end" | null) => {
			if (cursor) {
				setMode("edit");
				setEditing({ key: cursor.key, caret });
			}
		},
		[cursor]
	);

	const CHORDS: Record<string, () => void> = useMemo(
		() => ({
			ee: () => edit(null),
			ei: () => edit("start"),
			ea: () => edit("end"),
			ss: () => setOverlay({ kind: "status" }),
			rt: () => setRole("task"),
			rp: () => setRole("project"),
			rn: () => setRole("plain"),
			rf: () => setRole("focus"),
			dr: () => setOverlay({ kind: "recurrence" }),
			dd: () => setOverlay({ kind: "date" }),
			td: () => schedule(today),
			tm: () => schedule(nextDay(today)),
			cd: () => schedule(null),
			tt: () => setOverlay({ kind: "tags" }),
			ct: () => {
				for (const row of selectionRows()) {
					run([{ name: "setTags", input: { nodeId: row.nodeId, tags: [] } }]);
				}
			},
			nn: toggleBody,
			cn: () => {
				for (const row of selectionRows()) {
					run([
						{
							name: "setBody",
							input: {
								nodeId: row.nodeId,
								body: null,
								baseContentVersion: store.contentVersionOf(row.nodeId)
							}
						}
					]);
				}
			},
			pp: togglePin,
			pl: () => setOverlay({ kind: "picker", purpose: "placement" }),
			cp: () => {
				for (const row of selectionRows()) {
					if (row.placementId) {
						run([{ name: "removePlacement", input: { nodeId: row.nodeId, containerId: row.containerId } }]);
					}
				}
			},
			ar: archiveOrRestore,
			mm: () => setOverlay({ kind: "picker", purpose: "move" }),
			ff: () => setOverlay({ kind: "search" }),
			uu: undo,
			// Палитра команд — обычный аккорд, как и всё остальное. Двойного `Shift` здесь
			// нет и не будет: `Shift` — модификатор, он приходит вместе с щелчками и
			// стрелками выделения, и «нажатие» его отличимо от «удержания» только по
			// таймеру. Палитра открывалась от быстрого `Shift+↓ Shift+↓` сама.
			aa: () => setOverlay({ kind: "palette" }),
			st: () => cursor && setSelectedKeys(new Set([cursor.key])),
			gt: () => options.onGo("/today"),
			gi: () => options.onGo("/inbox"),
			gp: () => options.onGo("/projects"),
			gc: () => options.onGo("/calendar"),
			gr: () => options.onGo("/recurring"),
			gd: () => options.onGo("/overdue"),
			ga: () => options.onGo("/archive"),
			gf: () => options.onGo("/pinned"),
			go: () => options.onGo("/focus")
		}),
		[
			cursor,
			setRole,
			schedule,
			today,
			selectionRows,
			run,
			store,
			toggleBody,
			togglePin,
			archiveOrRestore,
			undo,
			options
		]
	);

	const CHORD_KEYS = useMemo(() => Object.keys(CHORDS), [CHORDS]);

	const handleChordChar = useCallback(
		(char: string) => {
			const next = chord + char;

			// `ec0…ec9` — свернуть до уровня N. «Развернуть всё» не берём принципиально:
			// циклы вплетений делают обход неограниченным (ui-spec Область 2).
			if (next.startsWith("ec") && next.length === 3 && /\d/.test(next[2]!)) {
				collapseStore.collapseToLevel(rows, Number(next[2]));
				setCollapseTick((t) => t + 1);
				resetChord();
				return;
			}

			const exact = CHORDS[next];
			if (exact) {
				exact();
				resetChord();
				return;
			}

			const isPrefix = next === "ec" || CHORD_KEYS.some((k) => k.startsWith(next));
			if (isPrefix) {
				setChord(next);
				if (chordTimer.current) {
					clearTimeout(chordTimer.current);
				}
				chordTimer.current = setTimeout(() => setChord(""), CHORD_TIMEOUT_MS);
				return;
			}

			// Не аккорд — начинаем заново с этого символа, а не глотаем его.
			resetChord();
			if (CHORD_KEYS.some((k) => k.startsWith(char)) || char === "e") {
				setChord(char);
				chordTimer.current = setTimeout(() => setChord(""), CHORD_TIMEOUT_MS);
			}
		},
		[chord, CHORDS, CHORD_KEYS, rows, resetChord]
	);

	const onKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (overlay !== null) {
				return; // оверлеи разбирают клавиши сами
			}

			if (mode === "edit") {
				return; // редактор строки обрабатывает сам, Esc возвращает сюда
			}

			const mod = event.metaKey || event.ctrlKey;
			// Буквы, цифры и `/` — по физической клавише: аккорды обязаны работать
			// и на русской раскладке теми же кнопками (см. lib/keyboard).
			const char = shortcutChar(event);

			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					if (mod) {
						moveVertically(1);
					} else {
						moveCursor(1, event.shiftKey);
					}
					return;
				case "ArrowUp":
					event.preventDefault();
					if (mod) {
						moveVertically(-1);
					} else {
						moveCursor(-1, event.shiftKey);
					}
					return;
				case "ArrowRight":
					event.preventDefault();
					if (event.shiftKey && cursor) {
						options.onZoom(cursor.nodeId);
					} else if (cursor?.hasChildren) {
						collapseStore.set_(cursor.key, false);
						setCollapseTick((t) => t + 1);
					}
					return;
				case "ArrowLeft":
					event.preventDefault();
					if (event.shiftKey) {
						const parent = replica.nodes.get(options.rootId)?.parentId;
						if (parent) {
							options.onZoom(parent);
						}
					} else if (cursor?.expanded) {
						collapseStore.set_(cursor.key, true);
						setCollapseTick((t) => t + 1);
					}
					return;
				case "Home":
					event.preventDefault();
					rows[0] && focusRow(rows[0].key);
					return;
				case "End":
					event.preventDefault();
					rows.length > 0 && focusRow(rows[rows.length - 1]!.key);
					return;
				// Голый `Enter` правит текущую строку — на выделенной строке человек читает его
				// как «войти в неё», а не «создать соседа». Соседа заводит `Cmd/Ctrl+Enter`,
				// дочернюю — `Shift+Enter`, верхнюю — `Alt+Enter` (модификаторы не складываем).
				case "Enter":
					event.preventDefault();
					if (event.shiftKey) {
						createSibling("child");
					} else if (event.altKey) {
						createSibling("above");
					} else if (mod || !cursor) {
						createSibling("below");
					} else {
						edit(null);
					}
					return;
				case "Tab":
					event.preventDefault();
					event.shiftKey ? outdent() : indent();
					return;
				case " ":
					event.preventDefault();
					if (event.shiftKey) {
						setStatus("cancelled");
					} else {
						toggleDone();
					}
					return;
				case "Delete":
				case "Backspace":
					event.preventDefault();
					removeSelection();
					return;
				case "Escape":
					event.preventDefault();
					resetChord();
					setSelectedKeys(new Set());
					setOverlay(null);
					return;
			}

			if (char === "/") {
				event.preventDefault();
				setOverlay({ kind: "search" });
				return;
			}

			if (mod) {
				switch (char) {
					case "a":
						event.preventDefault();
						if (cursor) {
							// `Cmd+a` берёт детей ТЕКУЩЕГО контейнера, не глубже.
							setSelectedKeys(
								new Set(rows.filter((r) => r.containerId === cursor.containerId).map((r) => r.key))
							);
						}
						return;
					case "c":
						event.preventDefault();
						copySelection("copy");
						return;
					case "x":
						event.preventDefault();
						copySelection("cut");
						return;
					case "v":
						event.preventDefault();
						if (event.shiftKey) {
							pasteLink();
						} else {
							void paste();
						}
						return;
					case "d":
						event.preventDefault();
						// Дубликат буфер НЕ трогает: он про строку под курсором, а не про то,
						// что человек копировал раньше.
						pasteNodes({ mode: "copy", nodeIds: selectionIds() });
						return;
					case "z":
						event.preventDefault();
						undo();
						return;
				}
				return;
			}

			if (char !== null && !event.altKey) {
				event.preventDefault();
				handleChordChar(char);
			}
		},
		[
			overlay,
			mode,
			cursor,
			rows,
			replica,
			options,
			moveCursor,
			moveVertically,
			focusRow,
			createSibling,
			edit,
			indent,
			outdent,
			toggleDone,
			setStatus,
			removeSelection,
			resetChord,
			copySelection,
			paste,
			pasteLink,
			pasteNodes,
			selectionIds,
			undo,
			handleChordChar
		]
	);

	const state: OutlineState = {
		rows,
		mode,
		cursorKey,
		selectedKeys,
		chord,
		editing,
		overlay,
		openBodies,
		flashing,
		rootId: options.rootId
	};

	const actions = {
		onKeyDown,
		focusRow,
		selectTo,
		toggleSelect,
		clearSelection,
		setMode,
		setEditing,
		stopEditing,
		setOverlay,
		setSelectedKeys,
		toggleCollapse: (key: string) => {
			collapseStore.toggle(key);
			setCollapseTick((t) => t + 1);
		},
		createSibling,
		edit,
		discardDraft,
		indent,
		outdent,
		moveVertically,
		toggleDone,
		setStatus,
		setRole,
		schedule,
		archiveOrRestore,
		removeSelection,
		togglePin,
		toggleBody,
		closeBody,
		copySelection,
		paste,
		pasteLink,
		pasteText,
		undo,
		run,
		selectionRows,
		selectionIds,
		zoom: options.onZoom,
		go: options.onGo,
		clipboard
	};

	return { state, actions };
}

function nextDay(date: string): string {
	const [y, m, d] = date.split("-").map(Number) as [number, number, number];
	const next = new Date(Date.UTC(y, m - 1, d + 1));
	return next.toISOString().slice(0, 10);
}
